import { useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import { getFulfillmentProvidersGridData } from '../api';
import {
  extractClientExternalConnectionId,
  extractClientExternalConnectionIdFromProviders,
  getArrayIfDataDoesNotExist,
  loadData,
} from '../utils';

const messages = defineMessages({
  fulfillmentServicesLoadErrorTitle: {
    description: 'Title for Error Pop Up when data grid failed to load.',
    defaultMessage: 'Failed to Load Data Grid',
    id: 'GRfQhJ',
  },
  fulfillmentServicesLoadErrorContent: {
    description: 'Content for Error Pop Up when data grid failed to load.',
    defaultMessage: 'An unexpected error occurred while loading Data Grid.',
    id: 'v+TnGm',
  },
});

const getFetchDataGridFn = (setDataGrid) => {
  return (clientExternalConnectionIds, externalInviteIds) => {
    if (!clientExternalConnectionIds?.length && !externalInviteIds?.length) {
      setDataGrid([]);
      return;
    }
    loadData(
      getFulfillmentProvidersGridData({
        clientExternalConnectionIds,
        externalInviteIds,
      }),
      (data) => setDataGrid(data?.data?.gridDataList),
      messages.fulfillmentServicesLoadErrorTitle,
      messages.fulfillmentServicesLoadErrorContent,
    );
  };
};

const getUpdatedDataGridProviders = (originalDataGridProviders, newDataGridProvider) => {
  const updatedClientExternalId = extractClientExternalConnectionId(newDataGridProvider);
  const dataGridProvidersWithoutOldProvider = originalDataGridProviders.filter(
    (counter) => extractClientExternalConnectionId(counter) !== updatedClientExternalId,
  );
  return [...dataGridProvidersWithoutOldProvider, newDataGridProvider];
};

const getUpdateDataGridProvidersFn = (setDataGrid, originalDataGridProviders, externalInviteIds) => {
  return () => {
    const countersToUpdate = extractClientExternalConnectionIdFromProviders(originalDataGridProviders).filter(
      (clientExternalConnectionIds) => !!clientExternalConnectionIds,
    );
    getFetchDataGridFn(setDataGrid)(countersToUpdate, externalInviteIds);
  };
};

const getUpdateDataGridProviderFn = (setDataGridProviders, originalDataGridProviders) => {
  return (providerId) => {
    const setUpdatedDataGridProviders = (dataGridProviders) => {
      // we always update single row, so this data is an array of 1
      const dataGridProvider = dataGridProviders[0];

      // so now we just update the counters array with the new counter
      const newDataGridProviders = getUpdatedDataGridProviders(originalDataGridProviders, dataGridProvider);

      setDataGridProviders(newDataGridProviders);
    };
    getFetchDataGridFn(setUpdatedDataGridProviders)([providerId]);
  };
};

export function useDataGrid(externalInviteIds, clientExternalConnectionIds) {
  const [dataGrid, setDataGrid] = useState([]);

  useEffect(() => {
    getFetchDataGridFn(setDataGrid)(clientExternalConnectionIds, externalInviteIds);
  }, [clientExternalConnectionIds, externalInviteIds]);

  return {
    dataGrid: getArrayIfDataDoesNotExist(dataGrid),
    updateDataGridProvider: getUpdateDataGridProviderFn(setDataGrid, dataGrid),
    updateDataGridProviders: getUpdateDataGridProvidersFn(setDataGrid, dataGrid, externalInviteIds),
  };
}
